import api from './api';

// Server logs see requests, not screens. A signup who reads a step and leaves,
// is refused by a check in the browser, or loses the page to a crash sends no
// request at all, and looks exactly like someone who never tried. These
// events are that missing record, sent only from the setup page.

const ENDPOINT = '/agents/onboarding-events';
const MAX_BATCH = 20;
const FLUSH_DELAY_MS = 2000;
const MAX_ERRORS_PER_PAGE = 5;
const MAX_TEXT = 300;

let queue = [];
let flushTimer = null;
let currentStep = null;
let errorsReported = 0;
const reportedErrors = new Set();
let installed = false;

export const onSetupPage = () => /\/get-started\/?$/.test(window.location.pathname || '');

// The API keeps snake_case keys and primitive values only; anything else is
// flattened here rather than silently dropped there.
const clean = (detail) => {
  const out = {};
  Object.entries(detail || {}).forEach(([key, value]) => {
    if (value === undefined) return;
    if (value === null || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value;
    } else {
      out[key] = String(value).slice(0, MAX_TEXT);
    }
  });
  return out;
};

const authHeaders = () => {
  const headers = { 'Content-Type': 'application/json' };
  const token = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || localStorage.getItem('user') || 'null');
    if (user?.tenant_id) headers['X-Tenant-ID'] = user.tenant_id;
  } catch {
    // A malformed stored user only loses the tenant hint; the token still identifies them.
  }
  return headers;
};

export const flushOnboardingEvents = ({ leaving = false } = {}) => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  while (queue.length) {
    const events = queue.slice(0, MAX_BATCH);
    queue = queue.slice(MAX_BATCH);
    try {
      const sent = leaving && typeof fetch === 'function'
        // The page is going away: an axios request would be cancelled with it.
        // keepalive lets the browser finish the send, and unlike sendBeacon it
        // can carry the Authorization header the endpoint requires.
        ? fetch(`${api.defaults.baseURL}${ENDPOINT}`, {
          method: 'POST',
          keepalive: true,
          headers: authHeaders(),
          body: JSON.stringify({ events }),
        })
        : api.post(ENDPOINT, { events });
      Promise.resolve(sent).catch(() => {});
    } catch {
      // Runs from timers and page-hide handlers: a throw here would surface as
      // a page error, the very thing this module exists to report.
    }
  }
};

export const setOnboardingStep = (step) => {
  currentStep = step || null;
};

// Telemetry must never be the thing that breaks setup, so this never throws
// and does nothing away from the setup page.
export const trackOnboarding = (name, detail = {}, step = currentStep) => {
  try {
    if (!onSetupPage()) return;
    queue.push({ name, step: step || null, detail: clean(detail), at: new Date().toISOString() });
    if (queue.length >= MAX_BATCH) {
      flushOnboardingEvents();
    } else if (!flushTimer) {
      flushTimer = setTimeout(() => flushOnboardingEvents(), FLUSH_DELAY_MS);
    }
  } catch {
    // Ignore: losing one event is better than losing the page.
  }
};

export const reportClientError = (error, detail = {}) => {
  if (!onSetupPage() || errorsReported >= MAX_ERRORS_PER_PAGE) return;
  const message = String(error?.message || error || 'Unknown error').slice(0, MAX_TEXT);
  // One broken render can fire the same error many times a second.
  if (reportedErrors.has(message)) return;
  reportedErrors.add(message);
  errorsReported += 1;
  trackOnboarding('client_error', {
    message,
    stack: String(error?.stack || '').split('\n').slice(0, 4).join(' | '),
    ...detail,
  });
  flushOnboardingEvents();
};

// A request path with ids removed, so failures group by endpoint.
const endpointOf = (config) => String(config?.url || '')
  .split('?')[0]
  .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '{id}');

export const installOnboardingTelemetry = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    reportClientError(event.error || event.message, {
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportClientError(event.reason, { kind: 'unhandled_rejection' });
  });

  // Every failed request the setup page makes, including the ones a screen
  // swallows into a generic message.
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      const endpoint = endpointOf(error.config);
      if (!endpoint.endsWith(ENDPOINT)) {
        const detail = error.response?.data?.detail;
        trackOnboarding('api_failed', {
          method: String(error.config?.method || '').toUpperCase(),
          endpoint,
          status: error.response?.status ?? null,
          detail: typeof detail === 'string' ? detail : error.message,
        });
      }
      return Promise.reject(error);
    },
  );

  // "Left the page" is the event abandonment otherwise leaves no trace of,
  // and a phone browser that drops a background tab reloads it later, which
  // shows up here as hidden, then a fresh wizard_loaded.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackOnboarding('page_hidden');
      flushOnboardingEvents({ leaving: true });
    } else {
      trackOnboarding('page_visible');
    }
  });
  window.addEventListener('pagehide', () => flushOnboardingEvents({ leaving: true }));
};
